import { useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import NavBar from '../components/layout/NavBar'
import { startProsSubscriptionCheckout } from '../api/pros'
import { getProsMarketing, getProsConsoleSummary, joinPros, type ProsMarketingPayload, type ProsConsoleSummary } from '../api/prosConsole'
import { getToken } from '../lib/auth'

const BASELINE: ProsMarketingPayload = {
  pro_console_requires_subscription: false,
  monthly_fee_usd: null,
  platform_fee_default_pct: 10,
  stake_required: true,
  stake_required_pixe: 200,
  deposit_policy: {
    required: true,
    percent: 20,
    min_pixe: 5,
    max_pixe: 200,
  },
  payout_policy: {
    requires_stripe_connect: true,
    currency: 'USD',
    note: 'Connect Stripe before withdrawals.',
  },
  benefits: [
    'Get discovered in Pixe People and receive direct invites.',
    'Clients fund PIXE escrow before work begins.',
    'Deliver -> approval releases earnings automatically.',
    'Track held vs earned PIXE in your Pro dashboard.',
    'Withdraw to USD through Stripe Connect when ready.',
  ],
  faq: [
    { q: 'How does escrow work?', a: 'Clients fund PIXE escrow before work starts. Funds are released on approval or auto-approval.' },
    { q: 'When do I get paid?', a: 'After delivery and approval, escrow releases into earned balance.' },
    { q: 'How do payouts work?', a: 'Connect Stripe and withdraw eligible earned balance to USD.' },
  ],
}

function Facts({ data }: { data: ProsMarketingPayload }) {
  const feeLabel = data.monthly_fee_usd == null ? 'No monthly fee' : `$${Number(data.monthly_fee_usd).toFixed(2)}/mo`
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2">
      <span className="rounded-full border border-emerald-300/35 bg-emerald-500/15 px-2 py-1 text-xs text-emerald-100">{feeLabel}</span>
      <span className="rounded-full border border-sky-300/35 bg-sky-500/15 px-2 py-1 text-xs text-sky-100">{data.platform_fee_default_pct}% platform fee</span>
      <span className="rounded-full border border-white/20 bg-white/10 px-2 py-1 text-xs text-white">Escrow protected</span>
    </div>
  )
}

function SkeletonBlock() {
  return <div className="h-24 animate-pulse rounded-2xl border border-white/10 bg-white/5" />
}

export default function ProsMarketingPage() {
  const navigate = useNavigate()
  const authed = Boolean(getToken())
  const [summary, setSummary] = useState<ProsConsoleSummary | null>(null)
  const [marketing, setMarketing] = useState<ProsMarketingPayload>(BASELINE)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    document.title = 'Pixe Pros — Get hired and get paid in PIXE'
    const existing = document.querySelector('meta[name="description"]')
    if (existing) {
      existing.setAttribute('content', 'Join Pixe Pros, get discovered, work with escrow protection, and track PIXE earnings.')
    }
  }, [])

  useEffect(() => {
    let mounted = true
    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const m = await getProsMarketing()
        if (!mounted) return
        setMarketing({ ...BASELINE, ...m })
      } catch (e: any) {
        if (!mounted) return
        setMarketing(BASELINE)
        setError(e?.response?.data?.detail || e?.message || 'Pros info unavailable.')
      }
      if (authed) {
        try {
          const s = await getProsConsoleSummary()
          if (mounted) setSummary(s)
        } catch {
          if (mounted) setSummary(null)
        }
      }
      if (mounted) setLoading(false)
    }
    void run()
    return () => {
      mounted = false
    }
  }, [authed])

  const requiresSubscription = Boolean(marketing.pro_console_requires_subscription)
  const escrowAnchor = '#trust-safety'

  const requirements = useMemo(() => {
    const out: string[] = []
    if (marketing.stake_required) out.push(`Stake ${marketing.stake_required_pixe} PIXE to activate.`)
    else out.push('No stake required to join right now.')
    out.push('Complete your profile and add at least one service.')
    if (marketing.payout_policy.requires_stripe_connect) out.push(marketing.payout_policy.note)
    return out
  }, [marketing])

  if (summary?.can_access_console) {
    return <Navigate to="/settings/pixe-pros?tab=overview" replace />
  }

  const onJoin = async () => {
    if (!authed) {
      navigate('/signup?return_to=%2Fpros')
      return
    }
    setBusy(true)
    setError(null)
    try {
      if (requiresSubscription && !summary?.pro_subscribed) {
        const checkout = await startProsSubscriptionCheckout()
        if (checkout?.url) {
          window.location.assign(checkout.url)
          return
        }
      }
      await joinPros()
      navigate('/settings/pixe-pros?tab=overview', { replace: true })
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Unable to join Pixe Pros right now.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen">
      <NavBar mobileFixed />
      <main className="mx-auto flex max-w-5xl flex-col gap-6 px-4 py-8">
        <section className="rounded-2xl border border-white/10 bg-white/5 p-6">
          <div className="text-xs uppercase tracking-[0.16em] text-white/55">PIXE PROS</div>
          <h1 className="mt-2 text-3xl font-extrabold text-white">Get hired and get paid in PIXE</h1>
          <p className="mt-2 max-w-2xl text-sm text-white/75">
            Build your Pro profile, receive requests, send quotes, and get paid through PIXE escrow.
          </p>
          <Facts data={marketing} />
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void onJoin()}
              disabled={busy || loading}
              className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
            >
              Join Pixe Pros
            </button>
            <Link to="/pros/discover" className="rounded-lg border border-white/15 bg-white/5 px-4 py-2 text-sm text-white hover:bg-white/10">
              Browse Pros
            </Link>
            <a href={escrowAnchor} className="text-sm text-brand-100 hover:text-brand-50">
              Learn how escrow works
            </a>
          </div>
        </section>

        {loading ? (
          <div className="grid gap-3">
            <SkeletonBlock />
            <SkeletonBlock />
            <SkeletonBlock />
          </div>
        ) : null}

        <section className="rounded-2xl border border-white/10 bg-white/5 p-6">
          <h2 className="text-lg font-semibold text-white">How it works</h2>
          <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-white/80">
            <li>Create your profile and add services.</li>
            <li>Receive requests and send PIXE quotes.</li>
            <li>Client funds PIXE escrow before work starts.</li>
            <li>Deliver work. Approval releases earnings.</li>
          </ol>
        </section>

        <section className="rounded-2xl border border-white/10 bg-white/5 p-6">
          <h2 className="text-lg font-semibold text-white">Why Pros use Pixeloop</h2>
          <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-white/80">
            {(marketing.benefits || BASELINE.benefits).map((benefit) => (
              <li key={benefit}>{benefit}</li>
            ))}
          </ul>
          <div className="mt-4 rounded-xl border border-brand/40 bg-brand/15 p-3 text-sm text-brand-100">
            Paid in PIXE. Escrow-protected. Withdraw to USD when you&apos;re ready.
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
            <h2 className="text-base font-semibold text-white">Requirements</h2>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-white/80">
              {requirements.map((row) => (
                <li key={row}>{row}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
            <h2 className="text-base font-semibold text-white">Fees</h2>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-white/80">
              <li>{marketing.monthly_fee_usd == null ? 'No monthly fee.' : `$${Number(marketing.monthly_fee_usd).toFixed(2)}/mo`}</li>
              <li>Platform fee: {marketing.platform_fee_default_pct}%</li>
              {marketing.deposit_policy.required ? (
                <li>
                  Escrow deposit: {marketing.deposit_policy.percent}% (min {marketing.deposit_policy.min_pixe} PIXE, max {marketing.deposit_policy.max_pixe} PIXE)
                </li>
              ) : (
                <li>No mandatory escrow deposit.</li>
              )}
            </ul>
          </div>
        </section>

        <section id="trust-safety" className="rounded-2xl border border-white/10 bg-white/5 p-6">
          <h2 className="text-lg font-semibold text-white">Trust &amp; Safety</h2>
          <div className="mt-3 grid gap-3 md:grid-cols-3">
            <div className="rounded-xl border border-white/10 bg-black/20 p-3 text-sm text-white/80">
              <div className="font-semibold text-white">Escrow protection</div>
              <div className="mt-1">Client deposits are held before work begins.</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-black/20 p-3 text-sm text-white/80">
              <div className="font-semibold text-white">Dispute support</div>
              <div className="mt-1">Disputes can pause release while resolution is in progress.</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-black/20 p-3 text-sm text-white/80">
              <div className="font-semibold text-white">Transparent fees</div>
              <div className="mt-1">Platform and payout requirements are visible before you join.</div>
            </div>
          </div>
        </section>

        <section id="faq" className="rounded-2xl border border-white/10 bg-white/5 p-6">
          <h2 className="text-lg font-semibold text-white">FAQ</h2>
          <div className="mt-3 space-y-2">
            {(marketing.faq || BASELINE.faq).map((item) => (
              <details key={item.q} className="rounded-xl border border-white/10 bg-black/20 p-3">
                <summary className="cursor-pointer text-sm font-semibold text-white">{item.q}</summary>
                <p className="mt-2 text-sm text-white/75">{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        {error ? (
          <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 p-3 text-sm text-amber-100">
            {error} Showing baseline policy details while services recover.
          </div>
        ) : null}
      </main>
    </div>
  )
}
